import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  BookOpen,
  CheckCircle2,
  ClipboardCheck,
  FileQuestion,
  GitBranch,
  Languages,
  Loader2,
  Medal,
  Plus,
  Upload,
} from 'lucide-react';

import { get, getJson } from '../../services/adminApi';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';

import CockpitHome from './CockpitHome';
import { CockpitPanel, CockpitTabs, CockpitWorkspaceContent } from './cockpit';
import { adminRoutes } from './navigation/adminRoutes';
import { getRememberedCockpitPath } from './navigation/adminPlaceMemory';
import { canAccessSection, getAdminUser } from './navigation/sectionAccess';

const DATA_SOURCES = [
  {
    key: 'vocabulary',
    endpoint: '/api/admin/vocabulary',
    label: 'vocabulary',
    itemLabel: 'Vocabulary',
    path: '/admin/learning/vocabulary',
    title: (item) => item.english || item.japanese,
  },
  {
    key: 'quizzes',
    endpoint: '/api/admin/quizzes',
    label: 'quizzes',
    itemLabel: 'Quiz',
    path: '/admin/learning/quizzes',
    title: (item) => item.title,
  },
  {
    key: 'scenarios',
    endpoint: '/api/admin/scenarios?include_inactive=true',
    label: 'scenarios',
    itemLabel: 'Scenario',
    path: '/admin/learning/scenarios',
    title: (item) => item.title,
  },
  {
    key: 'badges',
    endpoint: '/api/admin/badges',
    label: 'badges',
    itemLabel: 'Badge',
    path: '/admin/learning/badges',
    title: (item) => item.name,
  },
  {
    key: 'reviews',
    endpoint: '/api/admin/review-queue?status=pending',
    label: 'answer reviews',
  },
  {
    key: 'resources',
    endpoint: '/api/admin/resources',
    label: 'resources',
    itemLabel: 'Resource',
    path: '/admin/students/resources',
    title: (item) => item.title,
  },
];

const EMPTY_DATA = Object.fromEntries(DATA_SOURCES.map(({ key }) => [key, null]));

const formatCount = (items) => (Array.isArray(items) ? items.length : '\u2014');

const formatItemDate = (value) => {
  if (!value) return 'Date unavailable';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Date unavailable';

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const LearningCockpitHome = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const adminUser = useMemo(() => getAdminUser(), []);
  const hasAccess = useCallback(
    (sectionId) => canAccessSection(sectionId, adminUser),
    [adminUser]
  );
  const sections = useMemo(
    () => adminRoutes.learning.sections.filter((section) => hasAccess(section.id)),
    [hasAccess]
  );
  const normalizedPath = location.pathname.replace(/\/+$/, '') || '/';
  const activeSection = sections.find((section) => section.path === normalizedPath);
  const [data, setData] = useState(EMPTY_DATA);
  const [loading, setLoading] = useState(true);
  const [failedSources, setFailedSources] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const loadDashboard = async () => {
      const results = await Promise.allSettled(
        DATA_SOURCES.map(async (source) => {
          const response = await get(source.endpoint);
          const items = await getJson(response);

          if (!Array.isArray(items)) {
            throw new Error(`${source.label} response was not a list`);
          }

          return items;
        })
      );

      if (cancelled) return;

      const nextData = { ...EMPTY_DATA };
      const failures = [];

      results.forEach((result, index) => {
        const source = DATA_SOURCES[index];
        if (result.status === 'fulfilled') {
          nextData[source.key] = result.value;
        } else {
          failures.push(source.label);
        }
      });

      setData(nextData);
      setFailedSources(failures);
      setLoading(false);
    };

    loadDashboard();

    return () => {
      cancelled = true;
    };
  }, []);

  const recentItems = useMemo(() => DATA_SOURCES
    .filter((source) => source.path && Array.isArray(data[source.key]))
    .flatMap((source) => data[source.key].map((item) => ({
      id: `${source.key}-${item.id}`,
      label: source.itemLabel,
      path: source.path,
      title: source.title(item) || `Untitled ${source.itemLabel.toLowerCase()}`,
      timestamp: item.updated_at || item.created_at,
    })))
    .filter((item) => item.timestamp)
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
    .slice(0, 5), [data]);

  const kpis = [
    {
      id: 'ungraded-responses',
      title: 'Ungraded responses',
      value: formatCount(data.reviews),
      subtitle: 'Short answers awaiting review',
      icon: ClipboardCheck,
      tone: data.reviews?.length ? 'warn' : undefined,
    },
    {
      id: 'vocabulary-terms',
      title: 'Vocabulary terms',
      value: formatCount(data.vocabulary),
      icon: Languages,
    },
    {
      id: 'quizzes',
      title: 'Quizzes',
      value: formatCount(data.quizzes),
      icon: FileQuestion,
    },
    {
      id: 'scenarios',
      title: 'Scenarios',
      value: formatCount(data.scenarios),
      icon: GitBranch,
    },
    {
      id: 'badges',
      title: 'Badges',
      value: formatCount(data.badges),
      icon: Medal,
    },
  ];

  const quickActions = [
    {
      id: 'grade-answers',
      label: 'Grade answers',
      icon: ClipboardCheck,
      path: '/admin/learning/reviews',
      sectionId: 'reviews',
    },
    {
      id: 'new-quiz',
      label: 'New quiz',
      icon: Plus,
      path: '/admin/learning/quizzes',
      sectionId: 'quizzes',
    },
    {
      id: 'upload-resource',
      label: 'Upload resource',
      icon: Upload,
      path: '/admin/students/resources',
      sectionId: 'resources',
    },
  ];

  const pendingReviews = Array.isArray(data.reviews) ? data.reviews.length : null;

  if (!activeSection) {
    return sections[0]
      ? <Navigate to={getRememberedCockpitPath('learning', sections, adminUser)} replace />
      : <Navigate to="/admin" replace />;
  }

  const panelId = `learning-cockpit-tab-panel-${activeSection.id}`;

  return (
    <CockpitHome
      categoryId="learning"
      description="Manage curriculum, assessments, achievements, and training resources."
      kpis={kpis}
      quickActions={quickActions}
      canAccessSection={hasAccess}
      showSectionLinks={false}
      overviewSummary={pendingReviews > 0 ? `${pendingReviews} to review` : undefined}
      alerts={failedSources.length > 0 && (
        <div
          role="status"
          className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            Some live metrics are unavailable: {failedSources.join(', ')}. Available data is still shown.
          </p>
        </div>
      )}
      overview={(
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <Card className="admin-card border border-stone-200 lg:col-span-1">
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Answer review queue</p>
                  <p className="mt-1 text-sm text-stone-500 dark:text-stone-400">
                    Grade short-answer quiz responses.
                  </p>
                </div>
                {loading ? (
                  <Loader2 aria-label="Loading answer reviews" className="h-5 w-5 animate-spin text-amber-600" />
                ) : pendingReviews > 0 ? (
                  <Badge className="bg-amber-100 text-amber-900 hover:bg-amber-100 dark:bg-amber-900/50 dark:text-amber-200">
                    {pendingReviews} pending
                  </Badge>
                ) : pendingReviews === 0 ? (
                  <CheckCircle2 aria-label="No answers awaiting review" className="h-5 w-5 text-green-600" />
                ) : (
                  <Badge variant="outline">Unavailable</Badge>
                )}
              </div>
              <Button
                className="mt-4 w-full"
                variant="adminSecondary"
                onClick={() => navigate('/admin/learning/reviews')}
              >
                <ClipboardCheck className="h-4 w-4" />
                {pendingReviews > 0 ? 'Review answers' : 'Open answer reviews'}
              </Button>
            </CardContent>
          </Card>

          <Card className="admin-card border border-stone-200 lg:col-span-2">
            <CardContent className="p-5">
              <div className="mb-3 flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-amber-700 dark:text-amber-400" />
                <div>
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Recently edited curriculum</p>
                  <p className="text-xs text-stone-500 dark:text-stone-400">Latest changes across learning content and resources</p>
                </div>
              </div>

              {loading ? (
                <div className="flex min-h-28 items-center justify-center">
                  <Loader2 aria-label="Loading recent curriculum" className="h-6 w-6 animate-spin text-amber-600" />
                </div>
              ) : recentItems.length > 0 ? (
                <div className="divide-y divide-stone-200 dark:divide-stone-700">
                  {recentItems.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className="flex w-full items-center justify-between gap-3 py-2.5 text-left first:pt-0 last:pb-0 hover:text-red-800 dark:hover:text-amber-300"
                      onClick={() => navigate(item.path)}
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium">{item.title}</span>
                        <span className="text-xs text-stone-500 dark:text-stone-400">{item.label}</span>
                      </span>
                      <span className="shrink-0 text-xs text-stone-500 dark:text-stone-400">
                        {formatItemDate(item.timestamp)}
                      </span>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="py-8 text-center text-sm text-stone-500 dark:text-stone-400">
                  No dated curriculum items yet.
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    >
      <CockpitTabs
        tabs={sections}
        active={activeSection.id}
        onChange={(sectionId) => {
          const section = sections.find((candidate) => candidate.id === sectionId);
          if (section) navigate(section.path);
        }}
        label="Learning and curriculum sections"
        idPrefix="learning-cockpit-tab"
      />

      <CockpitPanel
        id={panelId}
        aria-labelledby={`learning-cockpit-tab-${activeSection.id}`}
      >
        <CockpitWorkspaceContent>
          <Outlet context={{ activeSection, activeSubsection: null }} />
        </CockpitWorkspaceContent>
      </CockpitPanel>
    </CockpitHome>
  );
};

export default LearningCockpitHome;
